/**
 * Arena family — the three inbound `arena_*` types.
 *
 * All 7 arena types ride ONE schemapack envelope
 * (`requestId/type/arenaId/tableId/payload`) whose `payload` is a JSON STRING,
 * so every handler starts with `parseArenaPayload` (`chess/arenaEnvelope.ts`).
 *
 * Functional spec = the OLD APP screen (`src/Screens/PlayChess/index.tsx`
 * L2927-3036), which writes `useArenaStore` DIRECTLY. V2 reduces into
 * `state.arena` and the PLATFORM BRIDGE mirrors the slice into that same store
 * (`../gamePlayStoreWrites`, `BRIDGED_ARENA_SETTERS`) so `TournamentPlay` and
 * `ArenaBerserkControl` keep reading what they always read.
 *
 * The three guards are the whole subtlety:
 *  1. status / standings: DROP when the envelope's `arenaId` is not the
 *     tournament we are on (a stale room subscription, L2933-2935);
 *  2. berserk: the same guard EXCEPT that a refusal arrives 1:1 with an EMPTY
 *     `arenaId`, so an empty id passes through (L2966-2972);
 *  3. berserk success is broadcast to the whole table AND the watch room, so it
 *     is acted on only when it is about ME or about the table I am seated at
 *     (L2977-2986) — otherwise a watched game pollutes my badges.
 *
 * Fix T / T4 (2026-10-06): `mergeStatus` additionally never REGRESSES a
 * known status — the owner's "games tab shows [empty] while playing" /
 * "Arena tab reads Starting soon after the arena has ended" reports
 * (`FIX_T_ARENA_CLARITY_SPEC.md` §0.1/§0.5, §1.3) both trace to the server's
 * own partial-broadcast / post-end-archived-fallback payloads carrying
 * falsy or `'unknown'` fields that, merged in naively, overwrote a perfectly
 * good known status. Two rules, in `mergeStatus` itself:
 *  1. a patch key whose value is `undefined`, `null` or `''` is DROPPED
 *     before the spread — it can never erase a known field;
 *  2. `state` is MONOTONIC along created -> running -> finishing ->
 *     finalizing -> finished|void: a patch may move it forward or hold it,
 *     never backward, and `'unknown'` never replaces a known state.
 * `statusResponse` then hands the bridge (`bridgeSetArenaStatus`) the
 * MERGED result, not the raw patch, so `ArenaStore`'s own merge (which also
 * does `{...previous, ...patch}`, unfiltered) cannot diverge from this
 * slice by re-deriving the same patch through a different, unprotected
 * path — see `ArenaStore.ts`'s `setStatus` (verified: it already merges,
 * so this is belt-and-suspenders there; it is the fix for a REPLACE).
 */
import {
  bridgeSetArenaBerserk,
  bridgeSetArenaStandings,
  bridgeSetArenaStatus,
} from '../gamePlayStoreWrites';
import {parseArenaPayload} from '../chess/arenaEnvelope';
import {ArenaSlice, ArenaStatus, LiveGameState, id} from '../state';

export const ARENA_TYPES = [
  'arena_status_response',
  'arena_standings_response',
  'arena_berserk_response',
];

const withArena = (
  state: LiveGameState,
  arena: Partial<ArenaSlice>,
): LiveGameState => ({...state, arena: {...state.arena, ...arena}});

/** the envelope is for the arena this surface is on. */
const sameArena = (state: LiveGameState, data: any): boolean =>
  id(data?.arenaId) === id(state.tournament.id);

/** created -> running -> finishing -> finalizing -> finished|void. */
const ARENA_STATE_ORDER: Record<string, number> = {
  created: 0,
  running: 1,
  finishing: 2,
  finalizing: 3,
  finished: 4,
  void: 4,
};

/**
 * T4.2 — `state` is MONOTONIC: a patch may move it forward along
 * `ARENA_STATE_ORDER` or hold it, never backward, and a value this table
 * does not recognise (the server's `'unknown'` fallback, §1.3 — or any
 * other unranked string) never replaces a KNOWN previous state. A state
 * neither side ranks (e.g. the very first status this arena has ever
 * reported IS `'unknown'`) is let through — there is nothing meaningful to
 * protect yet.
 */
const resolveArenaState = (
  previous: string | undefined,
  next: string | undefined,
): string | undefined => {
  if (next === undefined) {
    return previous;
  }
  const prevRank =
    previous !== undefined ? ARENA_STATE_ORDER[previous] : undefined;
  if (prevRank === undefined) {
    return next;
  }
  const nextRank = ARENA_STATE_ORDER[next];
  return nextRank !== undefined && nextRank >= prevRank ? next : previous;
};

/** T4.1 — drop keys whose patch value is `undefined`, `null` or `''`. */
const dropEmpty = (patch: any): any => {
  const clean: any = {};
  Object.keys(patch || {}).forEach(key => {
    const value = patch[key];
    if (value !== undefined && value !== null && value !== '') {
      clean[key] = value;
    }
  });
  return clean;
};

/**
 * PARTIAL payloads MERGE. The finish broadcast is `{state: 'finishing'}` alone;
 * replacing wholesale wiped myState/points/streak/onFire/berserkEnabled and hid
 * the berserk button for the whole first game (ArenaStore L110-114).
 *
 * T4: the merge is no longer a blind spread — see the two rules in this
 * file's header comment (T4.1 drops empty keys, T4.2 keeps `state`
 * monotonic). Both apply to every caller of this function unconditionally;
 * neither changes the "partial payloads merge" contract above.
 *
 * T2.2 (the Fix T review): EXPORTED so `reducers/game.ts` and
 * `reducers/tournament.ts` can drop their own `mergeArenaStatus` — those were
 * blind spreads with neither of the two protections above, used at exactly
 * the three call sites a partial/archived-fallback payload can regress a
 * known status (`startGameResponse`, `arenaGameEndSlice`, withdraw). No
 * import cycle: neither `game.ts` nor `tournament.ts` is imported here (or
 * by `../gamePlayStoreWrites`, which this file also imports), so a plain
 * sibling import is enough — no `reducers/arenaStatus.ts` leaf needed.
 */
export const mergeStatus = (
  previous: ArenaStatus | null,
  patch: any,
): ArenaStatus => {
  const clean = dropEmpty(patch);
  const merged = {...(previous || {}), ...clean} as ArenaStatus;
  return {
    ...merged,
    state: resolveArenaState(previous?.state, clean.state) as string,
  };
};

export function arenaReducer(
  state: LiveGameState,
  type: string,
  data: any,
  now: number,
): LiveGameState {
  switch (type) {
    case 'arena_status_response':
      return statusResponse(state, data);
    case 'arena_standings_response':
      return standingsResponse(state, data);
    case 'arena_berserk_response':
      return berserkResponse(state, data, now);
    default:
      return state;
  }
}

/** arena_status_response — L2927-2939. 1:1, and the only per-user source. */
function statusResponse(state: LiveGameState, data: any): LiveGameState {
  if (!sameArena(state, data)) {
    return state;
  }
  const payload = parseArenaPayload(data);
  const status = mergeStatus(state.arena.status, payload);
  // T4.3: the bridge gets the MERGED result, not the raw patch.
  bridgeSetArenaStatus(status);
  return withArena(state, {status});
}

/**
 * arena_standings_response — L2941-2956. A `JSON.parse` failure yields {}, so a
 * payload with no `entries` is DROPPED rather than wiping good standings.
 */
function standingsResponse(state: LiveGameState, data: any): LiveGameState {
  if (!sameArena(state, data)) {
    return state;
  }
  const payload = parseArenaPayload(data);
  if (!payload || !payload.entries) {
    return state;
  }
  bridgeSetArenaStandings(payload);
  return withArena(state, {standings: payload});
}

/**
 * arena_berserk_response — L2958-3011.
 *
 * Success (a table broadcast): `{userId, newClockMs, incrementMs}` — raise the
 * right badge and HALVE the displayed clock of whoever berserked.
 * Refusal (1:1, empty `arenaId`): `{userId, refused: reason}` — no badge; the
 * reason is recorded so the screen can say it (the old screen pops an `Alert`,
 * the web a toast — a reducer can do neither).
 */
function berserkResponse(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  // a refusal carries an empty arenaId, so only a NON-empty mismatch is stale
  if (data?.arenaId && !sameArena(state, data)) {
    return state;
  }
  const payload = parseArenaPayload(data);
  const who = id(payload?.userId);
  const isMe = !!state.session.me && who === state.session.me;
  const isMyTable =
    !!data?.tableId &&
    state.game.tableId !== null &&
    String(data.tableId) === String(state.game.tableId);
  if (!isMe && !isMyTable) {
    return state;
  }

  // L2984, before the refusal branch: the store early-returns on `refused`, so
  // passing it through cannot raise a badge.
  bridgeSetArenaBerserk(payload?.userId, payload || {}, state.session.me);

  if (payload?.refused) {
    // L2988-3007: only the player who was refused is told.
    return isMe
      ? withArena(state, {
          berserkRefusal: {reason: String(payload.refused), at: now},
        })
      : state;
  }

  const next = withArena(state, {
    myBerserk: isMe ? true : state.arena.myBerserk,
    opponentBerserk: isMe ? state.arena.opponentBerserk : true,
    berserkRefusal: null,
  });

  if (!isMyTable || payload?.newClockMs == null) {
    return next;
  }
  // L3005-3011: `newClockMs` is milliseconds and replaces the side's clock.
  // The extrapolation anchor is re-stamped ONLY when the clock that changed is
  // the one that is running, or the other side would be handed the elapsed time.
  const isWhite = who === id(next.game.whiteId);
  const running = id(next.game.turnUserId) === who;
  const newClockMs = Number(payload.newClockMs);
  return {
    ...next,
    game: {
      ...next.game,
      whiteTimeMs: isWhite ? newClockMs : next.game.whiteTimeMs,
      blackTimeMs: isWhite ? next.game.blackTimeMs : newClockMs,
      clockRunningSince: running ? now : next.game.clockRunningSince,
    },
  };
}
